import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Row } from 'react-bootstrap';
import { CustomCard, CustomCardBody } from '_components/Notifications/styles';
import { CustomCardFooter } from '_components/Financial/ContractGeneration/styles';
import { Button, Tag } from '_components/_core';
import { LuArrowLeft, LuSettings, LuTrash } from 'react-icons/lu';
import { sortBy } from 'lodash';
import { IoAdd } from 'react-icons/io5';
import { useHistory } from 'react-router-dom';

import { GatewayForm } from '_components/_shared';

function Gateways({
  activeCompany,

  availablePaymentGateways,
  paymentGateways,

  onDeletePaymentGateway,
  onFetchPaymentGateways,
  onFetchAvailablePaymentGateways,
}) {
  const history = useHistory();
  const [isGatewayFormOpen, setIsGatewayFormOpen] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);

  useEffect(() => {
    onFetchPaymentGateways();
    onFetchAvailablePaymentGateways();
  }, [onFetchPaymentGateways, onFetchAvailablePaymentGateways, activeCompany]);

  const handleEditGateway = useCallback(
    id => {
      setSelectedGateway(paymentGateways.find(item => item.id === id));
      setIsGatewayFormOpen(true);
    },
    [paymentGateways],
  );

  const handleDeleteGateway = useCallback(
    id => {
      onDeletePaymentGateway(id);
    },
    [onDeletePaymentGateway],
  );

  const configuredGateways = useMemo(() => {
    const configuredGatewaysIds = paymentGateways.map(
      gateway => gateway.payment_gateway_id,
    );

    return sortBy(availablePaymentGateways, 'name').filter(gateway =>
      configuredGatewaysIds.includes(gateway.id),
    );
  }, [paymentGateways, availablePaymentGateways]);

  const unconfiguredGateways = useMemo(() => {
    const configuredGatewaysIds = paymentGateways.map(
      gateway => gateway.payment_gateway_id,
    );

    return sortBy(availablePaymentGateways, 'name').filter(
      gateway => !configuredGatewaysIds.includes(gateway.id),
    );
  }, [availablePaymentGateways, paymentGateways]);

  const handleAddGateway = useCallback(() => {
    setSelectedGateway(null);
    setIsGatewayFormOpen(true);
    onFetchPaymentGateways();
  }, [onFetchPaymentGateways]);

  const renderList = useCallback(
    gateways => {
      return (
        <Row className="mt-3">
          {gateways.map(({ id, name, description, avatar_url }) => {
            const companyGateway = paymentGateways.find(
              gateway => gateway.payment_gateway_id === id,
            );

            return (
              <Col key={id} md={4} className="mb-3">
                <CustomCard>
                  <CustomCardBody>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex justify-content-center align-items-center">
                        <img
                          src={avatar_url}
                          alt="icon"
                          width={36}
                          style={{ borderRadius: '4px' }}
                        />
                        <h6 className="m-0 ml-2">{name}</h6>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="p-0 m-0">{description}</p>
                    </div>
                  </CustomCardBody>
                  <CustomCardFooter>
                    <span
                      className="d-flex justify-content-between align-items-center"
                      style={{ flex: 1 }}
                    >
                      <Tag variant={companyGateway ? 'success' : 'default'}>
                        {companyGateway ? 'Configurado' : 'Não configurado'}
                      </Tag>

                      <div className="d-flex justify-content-center align-items-center">
                        <Button
                          size="sm"
                          variant="default"
                          className="d-flex justify-content-center align-items-center"
                          onClick={() =>
                            companyGateway
                              ? handleEditGateway(companyGateway.id)
                              : handleAddGateway()
                          }
                          isLoading={false}
                          disabled={false}
                        >
                          <LuSettings />
                        </Button>
                        {companyGateway && (
                          <Button
                            size="sm"
                            variant="inverse-danger"
                            className="ml-2 d-flex justify-content-center align-items-center"
                            onClick={() => handleDeleteGateway(companyGateway.id)}
                            disabled={false}
                            isLoading={false}
                          >
                            <LuTrash />
                          </Button>
                        )}
                      </div>
                    </span>
                  </CustomCardFooter>
                </CustomCard>
              </Col>
            );
          })}
        </Row>
      );
    },
    [paymentGateways, handleEditGateway, handleAddGateway, handleDeleteGateway],
  );

  return (
    <Container fluid className="content-wrapper">
      <GatewayForm
        isOpen={isGatewayFormOpen}
        gateway={selectedGateway}
        onToggleForm={() => setIsGatewayFormOpen(!isGatewayFormOpen)}
        title={selectedGateway ? 'Editar Gateway' : 'Novo Gateway'}
        onAfterSaveCallback={() => {
          onFetchPaymentGateways();
          setIsGatewayFormOpen(false);
        }}
      />
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div className="d-flex flex-column align-items-start">
          <Button
            variant="link"
            size="sm"
            className="p-0 m-0 d-flex align-items-center justify-content-center"
            onClick={() => {
              history.goBack();
            }}
          >
            <LuArrowLeft className="mr-2" />
            Voltar
          </Button>
          <h4 className="mt-2">Provedores de pagamento</h4>
        </div>
        <div className="d-flex align-items-center">
          <Button
            variant="dark"
            icon={<IoAdd size="1.2em" />}
            iconPosition="left"
            disabled={false}
            onClick={handleAddGateway}
            name="add-gateway-button"
            size="sm"
          >
            Novo Gateway
          </Button>
        </div>
      </div>
      <div className="mt-3">
        {renderList(configuredGateways)}
        <h6 className="mt-3">Provedores disponíveis</h6>
        {renderList(unconfiguredGateways)}
      </div>
    </Container>
  );
}

Gateways.propTypes = {
  paymentGateways: PropTypes.array.isRequired,
  paymentMethods: PropTypes.array.isRequired,
  paymentMethodGateways: PropTypes.array.isRequired,
  onFetchPaymentGateways: PropTypes.func.isRequired,
  onFetchPaymentMethods: PropTypes.func.isRequired,
  onFetchPaymentMethodGateway: PropTypes.func.isRequired,
  onDeletePaymentGateway: PropTypes.func.isRequired,

  onFetchAvailablePaymentGateways: PropTypes.func.isRequired,
};

export default Gateways;
